import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { Box, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import type { TelemetryReplayControls } from '../../lib/useTelemetryReplay';
import { subscribeToPlayheadTicks } from '../../lib/useTelemetryReplay';

type GoProSyncedVideoOverlayProps = {
  videoSrc: string;
  replay: TelemetryReplayControls;
  layout?: 'overlay' | 'inline';
  containerEl?: HTMLElement | null;
  clipStartTimecodeMs?: number;
  clipEndTimecodeMs?: number;
  videoStartTimecodeMs?: number;
  storageKey?: string;
  title?: string;
  onAdjustTimingMs?: (deltaMs: number) => void;
  onAlignCurrentFrame?: (videoCurrentTimeSec: number) => void;
  onVideoDuration?: (durationSec: number) => void;
  timingAdjustmentDisabled?: boolean;
};

type OverlayCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type OverlayLayout = {
  corner: OverlayCorner;
  width: number;
};

const STORAGE_KEY = 'kangaroo.gopro-video-overlay-layout';
const DEFAULT_VIDEO_ASPECT = 16 / 9;
const EDGE_MARGIN = 12;
const BOTTOM_INSET = 72;
const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 180;
const MAX_WIDTH = 960;
const DRAG_BAR_HEIGHT = 22;
const TIMING_NUDGE_OPTIONS = [
  { label: '.10s', ms: 100 },
  { label: '.25s', ms: 250 },
  { label: '.50s', ms: 500 },
  { label: '1s', ms: 1000 },
];

function loadOverlayLayout(storageKey = STORAGE_KEY): OverlayLayout {
  if (typeof window === 'undefined') {
    return { corner: 'top-left', width: DEFAULT_WIDTH };
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return { corner: 'top-left', width: DEFAULT_WIDTH };
    }

    const parsed = JSON.parse(raw) as Partial<OverlayLayout>;
    const corner = parsed.corner;
    const width = parsed.width;
    if (
      (corner === 'top-left' || corner === 'top-right' || corner === 'bottom-left' || corner === 'bottom-right')
      && typeof width === 'number'
      && Number.isFinite(width)
    ) {
      return {
        corner,
        width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width)),
      };
    }
  } catch {
    // Ignore invalid persisted layout.
  }

  return { corner: 'top-left', width: DEFAULT_WIDTH };
}

function saveOverlayLayout(layout: OverlayLayout, storageKey = STORAGE_KEY) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch {
    // Ignore quota errors.
  }
}

function videoAspectFromMetadata(video: HTMLVideoElement) {
  return video.videoWidth > 0 && video.videoHeight > 0
    ? video.videoWidth / video.videoHeight
    : null;
}

function overlayHeight(width: number, videoAspect: number) {
  return width / videoAspect + DRAG_BAR_HEIGHT;
}

function clampWidth(width: number, bounds: { width: number; height: number }, videoAspect: number) {
  if (bounds.width <= 0 || bounds.height <= 0) {
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
  }

  const maxByWidth = bounds.width - EDGE_MARGIN * 2;
  const maxByHeight = (bounds.height - EDGE_MARGIN - BOTTOM_INSET) * videoAspect;
  const maxByBounds = Math.min(MAX_WIDTH, maxByWidth, maxByHeight);
  if (!Number.isFinite(maxByBounds) || maxByBounds < MIN_WIDTH) {
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));
  }

  return Math.min(maxByBounds, Math.max(MIN_WIDTH, width));
}

function positionForCorner(
  corner: OverlayCorner,
  width: number,
  bounds: { width: number; height: number },
  videoAspect: number,
) {
  const height = overlayHeight(width, videoAspect);
  const maxLeft = Math.max(EDGE_MARGIN, bounds.width - width - EDGE_MARGIN);
  const maxTop = Math.max(EDGE_MARGIN, bounds.height - height - BOTTOM_INSET);

  switch (corner) {
    case 'top-left':
      return { left: EDGE_MARGIN, top: EDGE_MARGIN };
    case 'top-right':
      return { left: maxLeft, top: EDGE_MARGIN };
    case 'bottom-left':
      return { left: EDGE_MARGIN, top: maxTop };
    case 'bottom-right':
      return { left: maxLeft, top: maxTop };
  }
}

function nearestCorner(
  left: number,
  top: number,
  width: number,
  bounds: { width: number; height: number },
  videoAspect: number,
): OverlayCorner {
  const height = overlayHeight(width, videoAspect);
  const candidates: Array<{ corner: OverlayCorner; left: number; top: number }> = [
    { corner: 'top-left', ...positionForCorner('top-left', width, bounds, videoAspect) },
    { corner: 'top-right', ...positionForCorner('top-right', width, bounds, videoAspect) },
    { corner: 'bottom-left', ...positionForCorner('bottom-left', width, bounds, videoAspect) },
    { corner: 'bottom-right', ...positionForCorner('bottom-right', width, bounds, videoAspect) },
  ];

  let best = candidates[0]!;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const centerDeltaX = (candidate.left + width / 2) - (left + width / 2);
    const centerDeltaY = (candidate.top + height / 2) - (top + height / 2);
    const distance = centerDeltaX ** 2 + centerDeltaY ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }

  return best.corner;
}

function resizeHandlePosition(corner: OverlayCorner) {
  switch (corner) {
    case 'top-left':
      return { right: 0, bottom: 0, cursor: 'nwse-resize' };
    case 'top-right':
      return { left: 0, bottom: 0, cursor: 'nesw-resize' };
    case 'bottom-left':
      return { right: 0, top: DRAG_BAR_HEIGHT, cursor: 'nesw-resize' };
    case 'bottom-right':
      return { left: 0, top: DRAG_BAR_HEIGHT, cursor: 'nwse-resize' };
  }
}

function useSyncedGoProVideo(
  videoRef: RefObject<HTMLVideoElement | null>,
  replay: TelemetryReplayControls,
  clipStartTimecodeMs?: number,
  clipEndTimecodeMs?: number,
  videoStartTimecodeMs = 0,
) {
  const clipStartSec = Math.max(0, ((clipStartTimecodeMs ?? videoStartTimecodeMs) - videoStartTimecodeMs) / 1000);
  const clipEndSec = clipEndTimecodeMs != null ? Math.max(clipStartSec, (clipEndTimecodeMs - videoStartTimecodeMs) / 1000) : null;

  const syncVideoToPlayhead = useCallback((playheadTimecodeMs: number) => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const rawTargetSec = (playheadTimecodeMs - videoStartTimecodeMs) / 1000;
    const inClip = rawTargetSec >= clipStartSec && (clipEndSec == null || rawTargetSec <= clipEndSec);
    const targetSec = clipEndSec == null
      ? Math.max(clipStartSec, rawTargetSec)
      : Math.min(clipEndSec, Math.max(clipStartSec, rawTargetSec));
    const driftSec = Math.abs(video.currentTime - targetSec);
    if (!inClip || !replay.isPlaying || driftSec > 0.35) {
      video.currentTime = targetSec;
    }
    if (!inClip) {
      video.pause();
    }
  }, [clipEndSec, clipStartSec, replay.isPlaying, videoRef, videoStartTimecodeMs]);

  useEffect(() => {
    if (replay.isPlaying) {
      return;
    }

    syncVideoToPlayhead(replay.getPlayheadTimecodeMs());
  }, [replay.isPlaying, replay.playheadTimecodeMs, syncVideoToPlayhead]);

  useEffect(() => {
    if (!replay.isPlaying) {
      return;
    }

    return subscribeToPlayheadTicks(() => {
      syncVideoToPlayhead(replay.getPlayheadTimecodeMs());
    });
  }, [replay.getPlayheadTimecodeMs, replay.isPlaying, syncVideoToPlayhead]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const clampToClipStart = () => {
      if (video.currentTime < clipStartSec) {
        video.currentTime = clipStartSec;
      }
    };

    clampToClipStart();
    video.addEventListener('loadedmetadata', clampToClipStart);
    return () => video.removeEventListener('loadedmetadata', clampToClipStart);
  }, [clipStartSec, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || clipEndSec == null) {
      return;
    }

    const clampToClipEnd = () => {
      if (video.currentTime >= clipEndSec) {
        video.currentTime = clipEndSec;
        video.pause();
      }
    };

    video.addEventListener('timeupdate', clampToClipEnd);
    return () => video.removeEventListener('timeupdate', clampToClipEnd);
  }, [clipEndSec, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.playbackRate = replay.playbackRate;
  }, [replay.playbackRate, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const rawTargetSec = (replay.getPlayheadTimecodeMs() - videoStartTimecodeMs) / 1000;
    const inClip = rawTargetSec >= clipStartSec && (clipEndSec == null || rawTargetSec <= clipEndSec);

    if (replay.isPlaying && inClip) {
      void video.play().catch(() => {
        // Autoplay can fail until the user interacts with the page.
      });
      return;
    }

    video.pause();
  }, [clipEndSec, clipStartSec, replay.getPlayheadTimecodeMs, replay.isPlaying, videoRef, videoStartTimecodeMs]);
}

function GoProInteractiveVideoOverlay({
  videoSrc,
  replay,
  containerEl,
  clipStartTimecodeMs,
  clipEndTimecodeMs,
  videoStartTimecodeMs = 0,
  storageKey = STORAGE_KEY,
  title = 'Footage',
  onAdjustTimingMs,
  onAlignCurrentFrame,
  onVideoDuration,
  timingAdjustmentDisabled = false,
}: {
  videoSrc: string;
  replay: TelemetryReplayControls;
  containerEl: HTMLElement | null;
  clipStartTimecodeMs?: number;
  clipEndTimecodeMs?: number;
  videoStartTimecodeMs?: number;
  storageKey?: string;
  title?: string;
  onAdjustTimingMs?: (deltaMs: number) => void;
  onAlignCurrentFrame?: (videoCurrentTimeSec: number) => void;
  onVideoDuration?: (durationSec: number) => void;
  timingAdjustmentDisabled?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [layout, setLayout] = useState<OverlayLayout>(() => loadOverlayLayout(storageKey));
  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const [videoAspect, setVideoAspect] = useState(DEFAULT_VIDEO_ASPECT);
  const [nudgeMs, setNudgeMs] = useState(250);
  const [dragOffset, setDragOffset] = useState<{ left: number; top: number } | null>(null);
  const dragStateRef = useRef<{ pointerId: number; startX: number; startY: number; originLeft: number; originTop: number } | null>(null);
  const resizeStateRef = useRef<{ pointerId: number; startX: number; startY: number; originWidth: number; corner: OverlayCorner } | null>(null);

  useSyncedGoProVideo(videoRef, replay, clipStartTimecodeMs, clipEndTimecodeMs, videoStartTimecodeMs);

  useEffect(() => {
    saveOverlayLayout(layout, storageKey);
  }, [layout, storageKey]);

  useEffect(() => {
    if (!containerEl) {
      return;
    }

    const updateBounds = () => {
      setBounds({
        width: containerEl.clientWidth,
        height: containerEl.clientHeight,
      });
    };

    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [containerEl]);

  const width = Math.max(MIN_WIDTH, bounds.width > 0 ? clampWidth(layout.width, bounds, videoAspect) : layout.width);
  const anchoredPosition = bounds.width > 0
    ? positionForCorner(layout.corner, width, bounds, videoAspect)
    : { left: EDGE_MARGIN, top: EDGE_MARGIN };
  const position = dragOffset ?? anchoredPosition;
  const handle = resizeHandlePosition(layout.corner);

  const clampPosition = useCallback((left: number, top: number, boxWidth: number) => {
    const height = overlayHeight(boxWidth, videoAspect);
    return {
      left: Math.min(Math.max(EDGE_MARGIN, left), Math.max(EDGE_MARGIN, bounds.width - boxWidth - EDGE_MARGIN)),
      top: Math.min(Math.max(EDGE_MARGIN, top), Math.max(EDGE_MARGIN, bounds.height - height - BOTTOM_INSET)),
    };
  }, [bounds.height, bounds.width, videoAspect]);

  const finishDrag = useCallback((left: number, top: number) => {
    if (bounds.width <= 0) {
      setDragOffset(null);
      return;
    }

    const corner = nearestCorner(left, top, width, bounds, videoAspect);
    setLayout((current) => ({ ...current, corner }));
    setDragOffset(null);
  }, [bounds, videoAspect, width]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (dragState && event.pointerId === dragState.pointerId) {
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        const next = clampPosition(
          dragState.originLeft + deltaX,
          dragState.originTop + deltaY,
          width,
        );
        setDragOffset(next);
        return;
      }

      const resizeState = resizeStateRef.current;
      if (resizeState && event.pointerId === resizeState.pointerId && bounds.width > 0) {
        const deltaX = event.clientX - resizeState.startX;
        const deltaY = event.clientY - resizeState.startY;
        let nextWidth = resizeState.originWidth;

        switch (resizeState.corner) {
          case 'top-left':
          case 'bottom-right':
            nextWidth = resizeState.originWidth + Math.max(deltaX, deltaY * videoAspect);
            break;
          case 'top-right':
          case 'bottom-left':
            nextWidth = resizeState.originWidth + Math.max(-deltaX, deltaY * videoAspect);
            break;
        }

        setLayout((current) => ({
          ...current,
          width: clampWidth(nextWidth, bounds, videoAspect),
        }));
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (dragState && event.pointerId === dragState.pointerId) {
        dragStateRef.current = null;
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        const next = clampPosition(
          dragState.originLeft + deltaX,
          dragState.originTop + deltaY,
          width,
        );
        finishDrag(next.left, next.top);
      }

      const resizeState = resizeStateRef.current;
      if (resizeState && event.pointerId === resizeState.pointerId) {
        resizeStateRef.current = null;
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [bounds, clampPosition, finishDrag, videoAspect, width]);

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (resizeStateRef.current) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: position.left,
      originTop: position.top,
    };
    setDragOffset({ left: position.left, top: position.top });
  };

  const startResize = (event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originWidth: width,
      corner: layout.corner,
    };
  };

  const toggleExpanded = () => {
    setLayout((current) => {
      const compactWidth = DEFAULT_WIDTH;
      const expandedWidth = bounds.width > 0 ? Math.min(MAX_WIDTH, Math.max(compactWidth, bounds.width * 0.72)) : 760;
      const targetWidth = current.width >= expandedWidth - 24 ? compactWidth : expandedWidth;
      return {
        ...current,
        width: clampWidth(targetWidth, bounds, videoAspect),
      };
    });
  };

  const isExpanded = bounds.width > 0 && width >= Math.min(MAX_WIDTH, bounds.width * 0.62);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: position.left,
        top: position.top,
        width,
        zIndex: 6,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(36,62,82,0.72)',
        bgcolor: '#000',
        boxShadow: '0 14px 42px rgba(0,0,0,0.45)',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <Box
        onPointerDown={startDrag}
        sx={{
          height: DRAG_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          cursor: 'grab',
          bgcolor: 'rgba(1,13,24,0.92)',
          borderBottom: '1px solid rgba(36,62,82,0.55)',
          color: 'rgba(148,163,184,0.9)',
          px: 0.65,
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.68rem',
            fontWeight: 850,
            color: '#CBD5E1',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        {onAdjustTimingMs ? (
          <>
            <Select
              size="small"
              value={nudgeMs}
              disabled={timingAdjustmentDisabled}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => setNudgeMs(Number(event.target.value))}
              sx={{
                height: 20,
                minWidth: 56,
                color: '#CBD5E1',
                fontSize: '0.62rem',
                fontWeight: 850,
                '.MuiSelect-select': { py: 0, pl: 0.65, pr: '20px !important' },
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.3)' },
                '.MuiSvgIcon-root': { color: '#94A3B8', fontSize: 16, right: 2 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#061423',
                    color: '#E2E8F0',
                    border: '1px solid rgba(36,62,82,0.72)',
                  },
                },
              }}
            >
              {TIMING_NUDGE_OPTIONS.map((option) => (
                <MenuItem key={option.ms} value={option.ms} sx={{ fontSize: '0.72rem' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title={`Video earlier by ${(nudgeMs / 1000).toFixed(nudgeMs < 1000 ? 2 : 0)}s`}>
              <span>
                <IconButton
                  aria-label="Move video earlier by selected increment"
                  size="small"
                  disabled={timingAdjustmentDisabled}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAdjustTimingMs(-nudgeMs);
                  }}
                  sx={{ width: 22, height: 18, color: '#CBD5E1', fontSize: '0.62rem', fontWeight: 900 }}
                >
                  -
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={`Video later by ${(nudgeMs / 1000).toFixed(nudgeMs < 1000 ? 2 : 0)}s`}>
              <span>
                <IconButton
                  aria-label="Move video later by selected increment"
                  size="small"
                  disabled={timingAdjustmentDisabled}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAdjustTimingMs(nudgeMs);
                  }}
                  sx={{ width: 22, height: 18, color: '#CBD5E1', fontSize: '0.62rem', fontWeight: 900 }}
                >
                  +
                </IconButton>
              </span>
            </Tooltip>
            {onAlignCurrentFrame ? (
              <Tooltip title="Align this video frame to the replay dot">
                <span>
                  <IconButton
                    aria-label="Align current video frame to replay position"
                    size="small"
                    disabled={timingAdjustmentDisabled}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      const currentTime = videoRef.current?.currentTime;
                      if (typeof currentTime === 'number' && Number.isFinite(currentTime)) {
                        onAlignCurrentFrame(currentTime);
                      }
                    }}
                    sx={{ width: 22, height: 18, color: '#A7F3D0' }}
                  >
                    <CenterFocusStrongIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </>
        ) : null}
        <Tooltip title={isExpanded ? 'Collapse video' : 'Expand video'}>
          <IconButton
            aria-label={isExpanded ? 'Collapse video' : 'Expand video'}
            size="small"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded();
            }}
            sx={{ width: 18, height: 18, color: '#CBD5E1' }}
          >
            {isExpanded ? <CloseFullscreenIcon sx={{ fontSize: 13 }} /> : <OpenInFullIcon sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="video"
        ref={videoRef}
        src={videoSrc}
        muted
        onLoadedMetadata={(event) => {
          const aspect = videoAspectFromMetadata(event.currentTarget);
          if (aspect != null) {
            setVideoAspect(aspect);
          }
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) {
            onVideoDuration?.(duration);
          }
        }}
        playsInline
        preload="metadata"
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: `${videoAspect} / 1`,
          objectFit: 'contain',
          pointerEvents: 'none',
        }}
      />

      <Box
        onPointerDown={startResize}
        sx={{
          position: 'absolute',
          width: 18,
          height: 18,
          ...handle,
          cursor: handle.cursor,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 4,
            borderRight: '2px solid rgba(148,163,184,0.85)',
            borderBottom: '2px solid rgba(148,163,184,0.85)',
            borderRadius: '0 0 4px 0',
            opacity: 0.9,
          },
        }}
      />
    </Box>
  );
}

export function GoProSyncedVideoOverlay({
  videoSrc,
  replay,
  layout = 'overlay',
  containerEl = null,
  clipStartTimecodeMs,
  clipEndTimecodeMs,
  videoStartTimecodeMs = 0,
  storageKey = STORAGE_KEY,
  title = 'Footage',
  onAdjustTimingMs,
  onAlignCurrentFrame,
  onVideoDuration,
  timingAdjustmentDisabled = false,
}: GoProSyncedVideoOverlayProps) {
  const inlineVideoRef = useRef<HTMLVideoElement | null>(null);
  const [inlineVideoAspect, setInlineVideoAspect] = useState(DEFAULT_VIDEO_ASPECT);
  useSyncedGoProVideo(inlineVideoRef, replay, clipStartTimecodeMs, clipEndTimecodeMs, videoStartTimecodeMs);

  if (layout === 'overlay') {
    return (
      <GoProInteractiveVideoOverlay
        videoSrc={videoSrc}
        replay={replay}
        containerEl={containerEl}
        clipStartTimecodeMs={clipStartTimecodeMs}
        clipEndTimecodeMs={clipEndTimecodeMs}
        videoStartTimecodeMs={videoStartTimecodeMs}
        storageKey={storageKey}
        title={title}
        onAdjustTimingMs={onAdjustTimingMs}
        onAlignCurrentFrame={onAlignCurrentFrame}
        onVideoDuration={onVideoDuration}
        timingAdjustmentDisabled={timingAdjustmentDisabled}
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        mb: 1.5,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(36,62,82,0.72)',
        bgcolor: '#000',
      }}
    >
      <Box
        component="video"
        ref={inlineVideoRef}
        src={videoSrc}
        muted
        onLoadedMetadata={(event) => {
          const aspect = videoAspectFromMetadata(event.currentTarget);
          if (aspect != null) {
            setInlineVideoAspect(aspect);
          }
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) {
            onVideoDuration?.(duration);
          }
        }}
        playsInline
        preload="metadata"
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: `${inlineVideoAspect} / 1`,
          objectFit: 'contain',
        }}
      />
    </Box>
  );
}
